import React, { useEffect, useState } from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { PDFViewer } from "@react-pdf/renderer";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import dayOfYear from "dayjs/plugin/dayOfYear";
import ReportTable from "./ReportTable";
import { axiosInstance } from "../../utils/networking";
dayjs.extend(utc);
dayjs.extend(dayOfYear);

const EMAIL_ACTIVITY_TYPES = [
  "Email Communication",
  "Provider Referral Initiated",
  "Providers Referral Email",
  "Event Invitation",
];

const isEmailActivity = (item: any) =>
  EMAIL_ACTIVITY_TYPES.includes(item?.activity_type);

const truncate = (value: any, max: number) => {
  const str = value == null ? "" : String(value);
  return str.length > max ? `${str.slice(0, max)}…` : str;
};

const arrayToObj = (arr: any) => {
  let obj: any = {};
  arr.map((item: any) => {
    obj[item._id] = item;
    return null;
  });
  return obj;
};

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#ffffff",
    padding: 20,
  },
  section: {
    margin: 10,
  },
  text: {
    fontFamily: "Times-Roman",
    fontSize: 12,
    marginBottom: 5,
  },
  table_row: {
    flexDirection: "row", // Arrange children horizontally
    alignItems: "center", // Align children vertically
    justifyContent: "space-between", // Center children horizontally
    margin: 0,
  },
  table_row_odd: {
    flexDirection: "row", // Arrange children horizontally
    alignItems: "center", // Align children vertically
    justifyContent: "space-between", // Center children horizontally
    margin: 0,
    backgroundColor: "rgb(235, 235, 235)",
  },
  table_text: {
    fontFamily: "Times-Roman",
    fontSize: 12,
    marginBottom: 2.5,
    marginTop: 2.5,
    marginLeft: 5,
    flex: 1,
  },
  checkbox_text: {
    fontFamily: "Times-Roman",
    fontSize: 12,
    marginBottom: 2.5,
    marginTop: 2.5,
    marginLeft: 2,
    flex: 1,
  },
  section_title: {
    fontSize: 18,
    color: "black",
    marginBottom: 10,
    marginTop: 10,
    paddingLeft: 10,
    paddingVertical: 5,
    fontWeight: "bold", // Bold font weight for section titles
    backgroundColor: "#e0e0e0", // Light grey background color
    borderLeftWidth: 6, // Left border
    borderLeftColor:
      process.env.REACT_APP_API_DOMAIN != "https://www.thrive-drive.org:8443"
        ? "rgb(63, 93, 125)"
        : "rgb(158, 31, 99)", // Border color matching the page title background
  },
  page_title: {
    fontSize: 24,
    color: "white",
    backgroundColor:
      process.env.REACT_APP_API_DOMAIN != "https://www.thrive-drive.org:8443"
        ? "rgb(63, 93, 125)"
        : "rgb(158, 31, 99)",
    marginBottom: 15,
    marginTop: 15,
    paddingLeft: 15,
    paddingVertical: 10,
    fontWeight: "bold", // Bold font weight
    textTransform: "uppercase", // Uppercase text
  },
  section_space: {
    marginTop: 40, // Adds space between section
  },

  image: {
    width: 100,
    height: 100,
  },
  resource_image: { maxWidth: "100%", maxHeight: "200px", padding: "10px" },
  mainTitle: {
    fontSize: 18,
    textAlign: "center",
    fontWeight: "bold",
  },
  header: {
    flexDirection: "row", // Arrange children horizontally
    alignItems: "center", // Align children vertically
    justifyContent: "space-between", // Center children horizontally
    margin: 0,
  },
  hr: {
    borderBottomColor: "black",
    borderBottomWidth: 1,
    width: "100%", // Make the line span the full width of the page
    marginTop: -20, // Add some margin at the bottom
    marginBottom: 10,
  },
  hr_nomargin: {
    borderBottomColor: "black",
    borderBottomWidth: 5,
    width: "100%", // Make the line span the full width of the page
    // marginTop: 10, // Add some margin at the bottom
    marginBottom: 10,
  },
  half_left: { textAlign: "left", width: "49%", height: "100%" },
  half_right: { textAlign: "left", width: "49%", height: "100%" },
  checkbox: {
    width: 10,
    height: 10,
    border: "1 solid black",
    display: "flex",
  },
  one_thired: {
    textAlign: "left",
    width: "33%",
    height: "100%",
    minHeight: "100px",
  },
  two_third: { textAlign: "left", width: "66%", height: "100%" },
  surveyTable: {
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  surveyTableCellHeader: {
    backgroundColor: "#E4E4E4",
    fontSize: 12,
    fontWeight: "bold",
  },
  surveyTableCell: {
    fontSize: 10,
  },
  surveyTableRow: {
    flexDirection: "row",
  },
  surveyTableCol: {
    width: "50%",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 5,
  },
  emailTable: {
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  emailTableRow: {
    flexDirection: "row",
  },
  emailTableCol: {
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 4,
  },
  emailTableColDate: { width: "15%" },
  emailTableColType: { width: "25%" },
  emailTableColSubject: { width: "38%" },
  emailTableColTo: { width: "22%" },
  emailTableCellHeader: {
    backgroundColor: "#E4E4E4",
    fontSize: 10,
    fontWeight: "bold",
  },
  emailTableCell: {
    fontSize: 9,
  },
  text_left: {
    textAlign: "left",
    fontFamily: "Times-Roman",
    fontSize: 12,
    marginBottom: 2.5,
    marginTop: 2.5,
    marginLeft: 5,
    flex: 1,
    whiteSpace: "normal",
    wordWrap: "break-word",
    wordBreak: "break-all",
    width: "33%",
  },
  text_right: {
    textAlign: "left",
    fontFamily: "Times-Roman",
    fontSize: 12,

    whiteSpace: "normal",
    wordWrap: "break-word",
    wordBreak: "break-all",
    width: "66%",
  },
});

const BFCReportPdf = (props: any) => {
  const startDate = props.startDate;
  const endDate = props.endDate;
  const [caseToFirmMapping, setCaseToFirmMapping] = useState<any>({});
  const [emails, setEmails] = useState<any>({});
  const [dataFlag, setDataFlag] = useState(false);
  const [caseInteractions, setCaseInteractions] = useState<any>({});
  const [reportData, setReportData] = useState<any>({});

  const startDateString = startDate ? startDate.format("YYYY-MM-DD") : "";
  const endDateShow = endDate || dayjs();
  const endDateString = endDateShow.format("YYYY-MM-DD");

  const [pdfReady, setPdfReady] = useState(false);

  useEffect(() => {
    if (pdfReady) {
      axiosInstance.post("/export_log", {
        export_type: "bfc_report_pdf",
        record_count: Object.keys(caseToFirmMapping).length,
        filters: {
          user_id: props.user?._id,
          startDate: startDateString,
          endDate: endDateString,
        },
      });
    }
  }, [pdfReady]);

  useEffect(() => {
    // No startDate = all time, no endDate = today (server defaults).
    const params: { [k: string]: string } = { userId: props.user._id };
    if (startDate) params.startDate = startDate.format("YYYY-MM-DD");
    if (endDate) params.endDate = endDate.format("YYYY-MM-DD");
    axiosInstance.get(`/user/bfcReport`, { params }).then((result) => {
      setEmails(result.data.emailActivitiesMapping);
      setCaseToFirmMapping(result.data.caseToFirmMapping);
      setCaseInteractions(result.data.caseToInteractionsMapping);
      setReportData(result.data.reportData);
      setDataFlag(true);
    });
  }, []);

  if (dataFlag) {
    return (
      <>
        <PDFViewer width="100%" height="700px">
          <Document
            onRender={() => {
              setPdfReady(true);
            }}
          >
            <Page size="LETTER" style={styles.page} key="reportData">
              <View>
                <Text style={styles.page_title}>Report Data</Text>
                {(startDate || endDate) && (
                  <Text style={styles.section_title}>
                    {startDateString || "Beginning"} to {endDateString}
                  </Text>
                )}
              </View>
              <ReportTable data={reportData} />
            </Page>
            {Object.keys(caseInteractions).map(
              (caseId: any, caseInteractionIndex: number) => {
                if (!caseToFirmMapping[caseId]) {
                  return <></>;
                }
                const caseActivities: any[] =
                  caseInteractions[caseId].activities || [];
                const bussAssessmentSurvey =
                  caseInteractions[caseId].surveyContent || [];
                const surveyResult = caseInteractions[caseId].surveyResult;

                const nonEmailActivities = caseActivities.filter(
                  (item: any) => !isEmailActivity(item)
                );
                const emailActivities = caseActivities.filter((item: any) =>
                  isEmailActivity(item)
                );

                // Skip pages for cases that were only emailed and have no survey.
                if (nonEmailActivities.length === 0 && !surveyResult) {
                  return <></>;
                }

                const firmName =
                  caseToFirmMapping && caseToFirmMapping[caseId]
                    ? caseToFirmMapping[caseId]["Company Name"] ||
                      "Name not available"
                    : "Name not available";
                return (
                  <Page size="LETTER" style={styles.page} key={`${caseId}`}>
                    <View>
                      <Text style={styles.page_title}>{firmName}</Text>
                    </View>
                    <View style={styles.section_space}>
                      <Text style={styles.section_title}>Activities</Text>
                    </View>
                    {nonEmailActivities.map((item: any, index: number) => {
                      return (
                        <View key={`${item._id || index}_${index}`} wrap={false}>
                          <View>
                            <Text>
                              {new Intl.DateTimeFormat("en-US").format(
                                new Date(item.created_at)
                              )}{" "}
                              - {item.activity_type}
                            </Text>
                            <View style={styles.hr_nomargin}></View>
                          </View>

                          <View style={styles.table_row}>
                            <Text style={styles.text_left}>Assistance:</Text>
                            <Text style={styles.text_right}>
                              {item.assistance_requested
                                ? item.assistance_requested.join(", ")
                                : ""}
                            </Text>
                          </View>

                          <View style={styles.table_row_odd}>
                            <Text style={styles.text_left}>Notes:</Text>
                            <Text style={styles.text_right}>
                              {item.activity_note}
                            </Text>
                          </View>

                          <View
                            style={{
                              flexDirection: "row",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Text style={styles.text}>
                              Contact Time:{item.activity_contact_time}
                            </Text>
                            <Text style={styles.text}>
                              Prep Time:{item.activity_prep_time}
                            </Text>
                            <Text style={styles.text}>
                              Travel Time:{item.activity_travel_time}
                            </Text>
                          </View>
                        </View>
                      );
                    })}
                    {emailActivities.length > 0 && (
                      <>
                        <View style={styles.section_space}>
                          <Text style={styles.section_title}>Emails sent</Text>
                        </View>
                        <View style={styles.emailTable}>
                          <View style={styles.emailTableRow} wrap={false}>
                            <View
                              style={[
                                styles.emailTableCol,
                                styles.emailTableColDate,
                                styles.emailTableCellHeader,
                              ]}
                            >
                              <Text>Date</Text>
                            </View>
                            <View
                              style={[
                                styles.emailTableCol,
                                styles.emailTableColType,
                                styles.emailTableCellHeader,
                              ]}
                            >
                              <Text>Type</Text>
                            </View>
                            <View
                              style={[
                                styles.emailTableCol,
                                styles.emailTableColSubject,
                                styles.emailTableCellHeader,
                              ]}
                            >
                              <Text>Subject</Text>
                            </View>
                            <View
                              style={[
                                styles.emailTableCol,
                                styles.emailTableColTo,
                                styles.emailTableCellHeader,
                              ]}
                            >
                              <Text>To</Text>
                            </View>
                          </View>
                          {emailActivities.map((item: any, index: number) => {
                            const email = emails?.[item?.email_id];
                            const subject = email
                              ? truncate(email.subject, 80)
                              : "(email not found)";
                            const to = email?.to || email?.toName || "";
                            return (
                              <View
                                style={styles.emailTableRow}
                                key={`${item._id || item.email_id || index}_${index}`}
                                wrap={false}
                              >
                                <View
                                  style={[
                                    styles.emailTableCol,
                                    styles.emailTableColDate,
                                  ]}
                                >
                                  <Text style={styles.emailTableCell}>
                                    {item.created_at
                                      ? new Intl.DateTimeFormat("en-US").format(
                                          new Date(item.created_at)
                                        )
                                      : ""}
                                  </Text>
                                </View>
                                <View
                                  style={[
                                    styles.emailTableCol,
                                    styles.emailTableColType,
                                  ]}
                                >
                                  <Text style={styles.emailTableCell}>
                                    {item.activity_type}
                                  </Text>
                                </View>
                                <View
                                  style={[
                                    styles.emailTableCol,
                                    styles.emailTableColSubject,
                                  ]}
                                >
                                  <Text style={styles.emailTableCell}>
                                    {subject}
                                  </Text>
                                </View>
                                <View
                                  style={[
                                    styles.emailTableCol,
                                    styles.emailTableColTo,
                                  ]}
                                >
                                  <Text style={styles.emailTableCell}>{to}</Text>
                                </View>
                              </View>
                            );
                          })}
                        </View>
                      </>
                    )}
                    <View style={styles.section_space}>
                      <Text style={styles.section_title}>
                        Business Assessments
                      </Text>
                    </View>
                    {surveyResult && (
                      <View style={styles.surveyTable}>
                        <View style={styles.surveyTableRow}>
                          <View
                            style={[
                              styles.surveyTableCol,
                              styles.surveyTableCellHeader,
                            ]}
                          >
                            <Text>Question</Text>
                          </View>
                          <View
                            style={[
                              styles.surveyTableCol,
                              styles.surveyTableCellHeader,
                            ]}
                          >
                            <Text>Answer</Text>
                          </View>
                        </View>
                        {bussAssessmentSurvey.map(
                          (content: any, index: number) => (
                            <View
                              style={styles.surveyTableRow}
                              key={content.name}
                            >
                              <View style={styles.surveyTableCol}>
                                <Text style={styles.surveyTableCell}>
                                  {content.pageTitle}
                                  {" - "}
                                  {content.title}
                                </Text>
                              </View>
                              <View style={styles.surveyTableCol}>
                                <Text style={styles.surveyTableCell}>
                                  {surveyResult[content.name] || ""}
                                </Text>
                              </View>
                            </View>
                          )
                        )}
                      </View>
                    )}
                  </Page>
                );
              }
            )}
          </Document>
        </PDFViewer>
        {!pdfReady && (
          <div className="absolute top-0 left-0 w-full h-[500px] z-10 flex flex-col items-center justify-center">
            <div className="w-12 h-12 border-4 border-gray-300 border-t-magenta rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-700 text-sm">
              Rendering report PDF... Please wait
            </p>
          </div>
        )}
      </>
    );
  } else {
    return (
      <div className="absolute top-0 left-0 w-full h-[500px] z-10 flex flex-col items-center justify-center">
        <div className="w-12 h-12 border-4 border-gray-300 border-t-magenta rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-700 text-sm">
          Fetching report data... Please wait
        </p>
      </div>
    );
  }
};

export default BFCReportPdf;
